import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useChangeBooleans, useComponentProperties, useResetAttributeFormBuilder, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import componentPropertiesMap from "./ComponentFormProperties.json";
import { useEffect, useState } from "react";
import AttributeFormBuilder from "../FormBuilder/AttributeFormBuilder";
import { useAttributeFormBuilder } from "../../../Hooks/Display/useComponentProperties";
import { useTranslation } from "react-i18next";

function View() {

    const labelOrientations = ["Top", "Left"];
    const columns = ["1", "2"];

    const formProperties = componentPropertiesMap.View;

    // hooks
    const selectedPage = useSelectedPage();
    const [{ steps }, dispatch] = useAttributeFormBuilder();
    const selectedComponent = useSelectedComponent();
    const isNewRecordPage = selectedPage?.subPageType === "New Record";
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const advancedMode = usePropsStoreValue("advancedMode");
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes();
    const resetContext = useResetComponentProperties();
    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const resetAttributes = useResetAttributeTable();
    const resetLayout = useResetAttributeFormBuilder();

    const [properties, setProperty] = useComponentProperties();
    const [{
        isGeneralPropertiesChanged,
        isAttributeFormBuilderChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();

    const { t, i18n } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/form"
    ])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            setProperties();
            setChangeProperty("isGeneralPropertiesChanged", false);
            setLoading(false);
        }
    }, [selectedPage._id, selectedComponent.id])

    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!loading) {
            setIsChanged();
        }
    }, [properties])

    // state
    const [tabSelected, setSelectedTab] = useState(0);

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function setProperties() {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperty(property, selectedComponent[property])
            }
        }
    }

    function handleInputChange(event) {
        setProperty(event.target.name, event.target.value);
    }

    function handleIsMultiStepFormChange(event) {
        setProperty(event.target.name, event.target.value);
        if (!event.target.value) {
            setProperty("allowToggleBetweenSteps", false);
            setProperty("showSteps", false);
            dispatch.removeMultipleSteps()
        } else {
            setProperty("allowToggleBetweenSteps", true);
            setProperty("showSteps", true);
        }
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            if (selectedComponent[property] !== properties[property]) {
                isChanged = true;
                break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent)
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setProperties();
        }
        if (isAttributeFormBuilderChanged) {
            resetLayout();
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2">
                                <div className="form-label">
                                    {t("form_labels.label_orientation", { ns: "display/components/form" })}
                                </div>
                                <DropDownList
                                    name="labelOrientation"
                                    disabled={selectedComponent.hidden}
                                    onChange={handleInputChange}
                                    className="form-control-custom w-25"
                                    value={properties.labelOrientation}
                                    data={labelOrientations}
                                />
                                <Checkbox
                                    name="collapsibleGroup"
                                    disabled={selectedComponent.hidden}
                                    checked={properties.collapsibleGroup}
                                    onChange={handleInputChange}
                                    label={t("form_labels.collapsible", { ns: "display/components/form" })}
                                    className="my-1 d-block"
                                />
                                <Checkbox
                                    name="isMultiStepForm"
                                    disabled={selectedComponent.hidden}
                                    checked={properties.isMultiStepForm}
                                    onChange={handleIsMultiStepFormChange}
                                    className='my-1 d-block'
                                    label={t("form_labels.is_multi_step_form", { ns: "display/components/form" })}
                                />
                                {
                                    advancedMode &&
                                    <Checkbox
                                        name="allowToggleBetweenSteps"
                                        disabled={true}
                                        onChange={handleInputChange}
                                        checked={properties.allowToggleBetweenSteps}
                                        className='my-1 d-block'
                                        label={t("form_labels.allow_toggle_between_steps", { ns: "display/components/form" })}
                                    />
                                }
                                <Checkbox
                                    name="showSteps"
                                    disabled={selectedComponent.hidden || !properties.isMultiStepForm}
                                    onChange={handleInputChange}
                                    checked={properties.showSteps}
                                    className='my-1 d-block'
                                    label={t("form_labels.show_steps", { ns: "display/components/form" })}
                                />
                                <div className="form-label mt-3">
                                    {t("field_actions.title", { ns: "display/components/form" })}
                                </div>
                                <div className="border rounded p-2 d-flex flex-wrap gap-3">
                                    <Checkbox
                                        name="fieldActionReset"
                                        disabled={selectedComponent.hidden}
                                        checked={properties.fieldActionReset}
                                        onChange={handleInputChange}
                                        label={t("field_actions.reset", { ns: "display/components/form" })}
                                    />
                                    <Checkbox
                                        name="fieldActionComment"
                                        disabled={selectedComponent.hidden}
                                        checked={properties.fieldActionComment}
                                        onChange={handleInputChange}
                                        label={t("field_actions.comment", { ns: "display/components/form" })}
                                    />
                                    {!isNewRecordPage && (
                                        <Checkbox
                                            name="fieldActionHistory"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.fieldActionHistory}
                                            onChange={handleInputChange}
                                            label={t("field_actions.history", { ns: "display/components/form" })}
                                        />
                                    )}
                                    <Checkbox
                                        name="fieldActionAttachment"
                                        disabled={selectedComponent.hidden}
                                        checked={properties.fieldActionAttachment}
                                        onChange={handleInputChange}
                                        label={t("field_actions.attachment", { ns: "display/components/form" })}
                                    />
                                </div>
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.form_builder", { ns: "display/common" })}>
                        <AttributeFormBuilder />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default View;